import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Folder, Lock, Users, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatRelativeTime } from 'lib/relativeTime';

/** The list endpoint carries no private/team/org flag, so the Library derives this best-effort per surface. */
export type CollectionShareState = 'private' | 'team' | 'org';

export const SHARE_META: Record<CollectionShareState, { icon: LucideIcon; labelKey: string }> = {
  private: { icon: Lock, labelKey: 'collection_share_private' },
  team: { icon: Users, labelKey: 'collection_share_team' },
  org: { icon: Building2, labelKey: 'collection_share_org' },
};

export interface CollectionCardProps {
  name: string;
  /** Undifferentiated test+question total — the only count the API returns. */
  itemCount: number;
  /** Last-updated timestamp; renders "· updated 2d ago" when present. */
  updatedAt?: string | Date | null;
  shareState?: CollectionShareState;
  /** Where the card leads, e.g. `/app/collections/:id`. */
  to: string;
  className?: string;
}

/** A 148px card: folder glyph and share tag on top, the name on two lines, counts at the foot. */
export const CollectionCard: React.FC<CollectionCardProps> = ({ name, itemCount, updatedAt, shareState, to, className }) => {
  const { t, i18n } = useTranslation();
  const share = shareState ? SHARE_META[shareState] : null;
  const ShareIcon = share?.icon;
  const updatedLabel = updatedAt ? formatRelativeTime(updatedAt, i18n.language) : '';

  return (
    <Link
      to={to}
      className={cn(
        'flex min-h-[148px] flex-col gap-2.5 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <Folder className="h-5 w-5 shrink-0 text-foreground/80" aria-hidden />
        {share && ShareIcon && (
          <span className="inline-flex h-[22px] items-center gap-[5px] rounded-md bg-muted px-2 text-xs font-medium text-foreground/80">
            <ShareIcon className="h-3 w-3" aria-hidden />
            {t(share.labelKey)}
          </span>
        )}
      </span>
      <span className="line-clamp-2 text-[14.5px] font-semibold leading-[1.35] text-foreground">{name}</span>
      <span className="mt-auto text-[12.5px] tabular-nums text-muted-foreground">
        {t('collections.items_count', { count: itemCount })}
        {updatedLabel && <> · {t('collections.updated_ago', { when: updatedLabel })}</>}
      </span>
    </Link>
  );
};

export default CollectionCard;
